import {
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Text,
} from '@optiaxiom/react';
import { IconXmark } from '@optimizely/axiom-icons';

import { ArtifactContainer, ArtifactProvider, Artifacts } from '@frontend/components/Artifact';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { useInstanceId } from '@frontend/hooks';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import {
  useGetAgentMemoryChunks,
  useGetSpecializedAgentExecution,
} from '../../../hooks/useSpecializedAgents';
import { StopExecutionButton } from '../../shared/StopExecutionButton';
import { TokenUsageSection } from '../../shared/TokenUsageSection';
import { EmptyStateMessage } from '../shared/EmptyStateMessage';
import { ExecutionDetailsCard } from '../shared/ExecutionDetailsCard';
import { ExecutionOutputDisplay } from '../shared/ExecutionOutputDisplay';
import { ExecutionStatusBadge } from '../shared/ExecutionStatusBadge';
import { MemoryChunksDisplay } from '../shared/MemoryChunksDisplay';

type SpecializedAgentExecutionDetailsSidebarProps = {
  executionId?: string;
  execution?: SpecializedAgentExecution;
  onClose: () => void;
};

export function SpecializedAgentExecutionDetailsSidebar({
  executionId,
  execution: providedExecution,
  onClose,
}: SpecializedAgentExecutionDetailsSidebarProps) {
  const { instanceId } = useInstanceId();

  // Fetch execution details only if no execution is provided and executionId exists
  const shouldFetchExecution = !providedExecution && !!executionId && !!instanceId;
  const {
    execution: fetchedExecution,
    isPending: isLoadingExecution,
    error: executionError,
  } = useGetSpecializedAgentExecution(
    shouldFetchExecution ? instanceId : '',
    shouldFetchExecution ? executionId : ''
  );

  const execution = providedExecution || fetchedExecution;
  const isStreaming = execution
    ? execution?.status.toLowerCase() !== 'completed' &&
      execution?.status.toLowerCase() !== 'failed'
    : false;
  const { memoryChunks } = useGetAgentMemoryChunks(
    execution?.memory_id,
    execution?.links?.memory,
    isStreaming
  );

  const formatJsonForMarkdown = (obj: unknown): string => {
    return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
  };

  if (isLoadingExecution && !providedExecution) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Loading Execution...
          </Text>
          <Button
            icon={<IconXmark size="16" />}
            appearance="default"
            onClick={() => {
              onClose();
            }}
            aria-label="Close sidebar"
          />
        </Flex>
        <Flex justifyContent="center" alignItems="center" h="224">
          <Text>Loading execution details...</Text>
        </Flex>
      </Flex>
    );
  }

  if (executionError || !execution) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Execution Details
          </Text>
          <Button
            icon={<IconXmark size="16" />}
            appearance="default"
            onClick={onClose}
            aria-label="Close sidebar"
          />
        </Flex>
        <EmptyStateMessage message="Failed to load execution details" />
      </Flex>
    );
  }

  return (
    <ArtifactProvider>
      <ArtifactContainer>
        <Flex flexDirection="column" gap="16" p="16">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Flex flexDirection="row" alignItems="center" gap="12">
              <Text fontWeight="600" fontSize="lg">
                Execution Details
              </Text>
              <ExecutionStatusBadge status={execution.status} showAsRunning />
            </Flex>
            <Flex flexDirection="row" gap="8" alignItems="center">
              {isStreaming && executionId && <StopExecutionButton executionId={executionId} />}
              <Button
                icon={<IconXmark size="16" />}
                appearance="default"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>
          </Flex>

          {/* Basic Info */}
          <ExecutionDetailsCard
            executionId={execution.id}
            status={execution.status}
            agentId={execution.agent_id}
            createdAt={execution.created_at}
            executionTime={execution.execution_time}
            tokenUsage={execution.token_usage}
            variant="detailed"
            hideStatusBadge
          />

          {/* Input Parameters */}
          <Disclosure defaultOpen>
            <DisclosureTrigger>
              <Field label="Input Parameters" info="Parameters used for this execution" />
            </DisclosureTrigger>
            <DisclosureContent>
              <Flex flexDirection="column" gap="16" px="16">
                {execution.parameters && Object.keys(execution.parameters).length > 0 ? (
                  <Field label="Parameters">
                    <Box border="1" borderColor="border.default" rounded="md">
                      <Box bg="bg.default" p="12">
                        <MarkdownText text={formatJsonForMarkdown(execution.parameters)} />
                      </Box>
                    </Box>
                  </Field>
                ) : (
                  <EmptyStateMessage message="No parameters were used for this execution" />
                )}
              </Flex>
            </DisclosureContent>
          </Disclosure>

          {/* Artifact Disclosure */}
          {execution.artifacts && execution.artifacts.length > 0 && (
            <Disclosure w="full" defaultOpen>
              <DisclosureTrigger>
                <Field label="Artifacts" info="Agent Execution Artifacts:" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Artifacts artifacts={execution?.artifacts} />
              </DisclosureContent>
            </Disclosure>
          )}
          {/* Output/Result */}
          <Disclosure defaultOpen>
            <DisclosureTrigger>
              <Field label="Output" info="Execution results and response" />
            </DisclosureTrigger>
            <DisclosureContent>
              <Flex flexDirection="column" gap="16" px="16">
                <Field label="Agent Response">
                  <ExecutionOutputDisplay
                    result={execution.result}
                    showStatus={false}
                    emptyMessage="No output available."
                    emptySubMessage="Execution did not produce any results."
                  />
                </Field>
              </Flex>
            </DisclosureContent>
          </Disclosure>

          {/* Log */}
          {execution.log && execution.log.length > 0 && (
            <Disclosure>
              <DisclosureTrigger>
                <Field label="Execution Log" info="Execution log messages" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16" px="16">
                  <Field label="Log Messages">
                    <Box border="1" borderColor="border.default" rounded="md">
                      <Box bg="bg.default" p="12">
                        {execution.log.map(logEntry => (
                          <Text
                            key={`log-entry-${logEntry.slice(0, 20).replace(/\s+/g, '-')}`}
                            fontSize="sm"
                            fontFamily="mono"
                            mb="4"
                          >
                            {logEntry}
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  </Field>
                </Flex>
              </DisclosureContent>
            </Disclosure>
          )}

          {/* Memory */}
          {execution.links?.memory && (
            <Disclosure defaultOpen>
              <DisclosureTrigger>
                <Field label="Memory" info="Agent memory chunks from execution" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16">
                  <MemoryChunksDisplay
                    memoryChunks={memoryChunks || []}
                    isStreaming={isStreaming}
                    emptyMessage="No memory chunks available"
                    emptySubMessage="Memory data will appear here when available."
                  />
                </Flex>
              </DisclosureContent>
            </Disclosure>
          )}

          {/* Token Usage */}
          <TokenUsageSection tokenUsage={execution.token_usage} />
        </Flex>
      </ArtifactContainer>
    </ArtifactProvider>
  );
}
